import React from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Close, MismatchGradient } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import Badge from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import IconContainer from "@/components/ui/icon-container";
import PopoverWrapper from "@/components/controls/Questions/DreamerIndication/PopoverWrapper";
import DreamerIcons from "@/components/controls/Questions/DreamerIndication/components/DreamerIcons";
import ResponseSources from "@/components/controls/Questions/DreamerIndication/components/ResponseSources";
import MismatchSection from "@/components/controls/Questions/DreamerIndication/components/MismatchIndication/MismatchSection";
import { usePopoverState } from "@/components/controls/Questions/DreamerIndication/hooks";
import { ReservedUserIds } from "@/entities/users/types";
import {
  DreamerSuggestionDetails,
  useResolveWorkflowItemSuggestion,
  WorkflowItemResponseDetails,
  WorkflowSuggestionAction
} from "@/pages/contracts/hooks";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { OnboardingWorkflowResponseValue } from "../types";
import { isAnswered } from "../scoreStatus";

const TRANSLATION_KEY = "contracts.detailedView.onboarding.dreamer";

interface Props {
  contractId: string;
  itemId: string;
  value: OnboardingWorkflowResponseValue;
  details: WorkflowItemResponseDetails | undefined;
  /** The value as plain text, so screen readers can read and compare both values. */
  renderValue: (value: OnboardingWorkflowResponseValue) => string;
  /** Called once the suggestion is accepted or dismissed on the server. */
  onResolved?: (action: WorkflowSuggestionAction) => void;
}

const FilledByDreamerIndication: React.FC<{
  contractId: string;
  details: WorkflowItemResponseDetails;
}> = ({ contractId, details }) => {
  const { t } = useTranslation();
  const { isPopoverOpen, setIsPopoverOpen } = usePopoverState();
  const label = t(`${TRANSLATION_KEY}.filledByDreamer`);
  const icons = (size: "md" | undefined) => (
    <DreamerIcons
      dreamerMode
      isLastEditedByDreamer
      lastEditedByData={undefined}
      previousLastEditedBy={null}
      size={size}
    />
  );

  return (
    <PopoverWrapper
      isPopoverOpen={isPopoverOpen}
      setIsPopoverOpen={setIsPopoverOpen}
      triggerContent={icons(undefined)}
      tooltipText={label}
      popoverContent={
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {icons("md")}
              <Text variant="lgLabel">{label}</Text>
            </div>
            <IconButton
              aria-label={t("close")}
              onClick={() => setIsPopoverOpen(false)}
              variant="iconNoBg"
              iconColor="secondary"
              size="md"
            >
              <Close />
            </IconButton>
          </div>
          <ResponseSources
            entityType={EntityType.CONTRACT}
            entityId={contractId}
            response={{
              activityId: details.activityId ?? undefined,
              citation: details.citation ?? undefined
            }}
          />
        </>
      }
    />
  );
};

const SuggestionIndication: React.FC<{
  contractId: string;
  itemId: string;
  value: OnboardingWorkflowResponseValue;
  suggestion: DreamerSuggestionDetails;
  renderValue: Props["renderValue"];
  onResolved: Props["onResolved"];
}> = ({ contractId, itemId, value, suggestion, renderValue, onResolved }) => {
  const { t } = useTranslation();
  const { isPopoverOpen, setIsPopoverOpen } = usePopoverState();
  const resolveSuggestion = useResolveWorkflowItemSuggestion();
  const title = t(`${TRANSLATION_KEY}.suggestion.title`);

  const handleResolve = (action: WorkflowSuggestionAction) => {
    resolveSuggestion.mutate(
      { pathParams: { contractId, itemId }, data: { action } },
      {
        onSuccess: () => {
          setIsPopoverOpen(false);
          onResolved?.(action);
        },
        onError: () => {
          toast.error(t(`${TRANSLATION_KEY}.suggestion.resolveFailed`));
        }
      }
    );
  };

  return (
    <PopoverWrapper
      isPopoverOpen={isPopoverOpen}
      setIsPopoverOpen={setIsPopoverOpen}
      tooltipText={title}
      useButtonWrapper={false}
      borderClassName="border border-border-border1"
      showBackgroundGradient={false}
      containerClassName="w-[35rem]"
      triggerContent={
        <Badge className="p-[2px] pr-[6px]">
          <div
            className="border-[0.5px] border-border-border2 rounded-lg p-[2px] backdrop-blur-lg"
            style={{
              background:
                "linear-gradient(180deg, var(--opacity-white-16, rgba(255, 255, 255, 0.16)) 0%, var(--opacity-black-24, rgba(12, 17, 29, 0.24)) 100%)"
            }}
          >
            <IconContainer>
              <MismatchGradient />
            </IconContainer>
          </div>
          <Text variant="bodySecondary">
            {t(`${TRANSLATION_KEY}.suggestion.trigger`)}
          </Text>
        </Badge>
      }
      popoverContent={
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <IconContainer size="lg">
                <MismatchGradient />
              </IconContainer>
              <Text variant="lgLabel">{title}</Text>
            </div>
            <IconButton
              aria-label={t("close")}
              onClick={() => setIsPopoverOpen(false)}
              variant="iconNoBg"
              iconColor="secondary"
              size="md"
            >
              <Close />
            </IconButton>
          </div>
          <div className="flex gap-2">
            <MismatchSection
              title={t(`${TRANSLATION_KEY}.suggestion.currentTitle`)}
              sourceLabel={t(`${TRANSLATION_KEY}.suggestion.dreamerSource`)}
              value={renderValue(value)}
              mode="current"
            />
            <MismatchSection
              title={t(`${TRANSLATION_KEY}.suggestion.dreamerTitle`)}
              sourceLabel={t(`${TRANSLATION_KEY}.suggestion.dreamerSource`)}
              value={renderValue(
                suggestion.value as OnboardingWorkflowResponseValue
              )}
              mode="dreamer"
              citation={suggestion.citation ?? undefined}
              footerActions={
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="secondary"
                    onClick={() => handleResolve("dismiss")}
                    disabled={resolveSuggestion.isPending}
                  >
                    {t("dismiss")}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleResolve("approve")}
                    disabled={resolveSuggestion.isPending}
                  >
                    {t(`${TRANSLATION_KEY}.suggestion.accept`)}
                  </Button>
                </div>
              }
            />
          </div>
        </>
      }
    />
  );
};

// A pending suggestion wins over the filled-by-Dreamer mark: it is the one that
// asks the user to act.
export const OnboardingItemDreamerIndication: React.FC<Props> = ({
  contractId,
  itemId,
  value,
  details,
  renderValue,
  onResolved
}) => {
  if (details?.dreamerSuggestion) {
    return (
      <SuggestionIndication
        contractId={contractId}
        itemId={itemId}
        value={value}
        suggestion={details.dreamerSuggestion}
        renderValue={renderValue}
        onResolved={onResolved}
      />
    );
  }
  if (
    details?.lastEditedBy === ReservedUserIds.DREAMER_CONVERSATIONAL_ID &&
    isAnswered(value)
  ) {
    return (
      <FilledByDreamerIndication contractId={contractId} details={details} />
    );
  }
  return null;
};
